import { Injectable, inject } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { BdUserTracker } from '@bindoc-projects/util-domain-lib';
import { BdWindowRefService } from '@bindoc-projects/platform-lib/data';
import { AccountInfo } from '@azure/msal-node';
import { BdUtilScriptService } from '@bindoc-projects/util-lib/data';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';

@Injectable({
  providedIn: 'root',
})
export class BdHubspotTrackingService extends BdUserTracker {
  private readonly _windowRefService = inject(BdWindowRefService);
  private readonly _scriptService = inject(BdUtilScriptService);
  private readonly _router = inject(Router);
  private readonly _authenticationService = inject(BdAuthenticationService);

  public name = 'BdHubspotTrackingService';

  private readonly _id = 'hs-script-loader';

  private isAuthenticatedUser: boolean = false;

  constructor() {
    super();

    this._authenticationService.authentication$.subscribe((authenticated) => {
      this.isAuthenticatedUser = authenticated;
    });
  }

  init(): void {
    this._scriptService.loadJsScript({
      id: this._id,
      async: true,
      src: '//js.hs-scripts.com/5251653.js',
      defer: true,
    });

    const routerEventsSub = this._router.events
      .pipe(
        filter(() => this.isAuthenticatedUser),
        filter(() => this.isEnabled),
        filter((event: Event) => event instanceof NavigationEnd),
      )
      .subscribe((routerEvent: Event) => {
        this.trackRoute(routerEvent as NavigationEnd);
      });

    this._subs.push(routerEventsSub);
    this.isEnabled = true;
  }

  destroy(): void {
    this._scriptService.removeJsScript(this._id);
    this.isEnabled = false;
    this._unsubscribe();
  }

  addAuthenticatedUserContext(accountInfo: AccountInfo | null): void {
    const window = this._windowRefService.get();
    const hsq = (window['_hsq'] = window['_hsq'] || []);

    if (accountInfo) {
      hsq.push([
        'identify',
        {
          email: accountInfo.username,
        },
      ]);
    }
  }

  removeAuthenticatedUserContext(): void {
    const window = this._windowRefService.get();

    // resetting the global object _hsq should delete all information, collected until remove of context
    window['_hsq'] = [];
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trackException(error: unknown): void {
    // noop: HubSpot does not support exception tracking
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trackEvent(event: string): void {
    // noop: HubSpot does not support custom event tracking
  }

  private trackRoute(event: NavigationEnd): void {
    const window = this._windowRefService.get();
    const hsq = (window['_hsq'] = window['_hsq'] || []);

    hsq.push(['setPath', event.url]);
    hsq.push(['trackPageView']);
  }
}
